import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Button } from "./ui/button"
import { Card } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Badge } from "./ui/badge"
import { Checkbox } from "./ui/checkbox"
import { UserPlus, Pencil, Trash2, Shield } from "lucide-react"
import { 
  getOrganizationUsersByUserId, 
  getUser, 
  postRegistrationUser, 
  updateOrganizationUser, 
  deleteOrganizationUser,
  getTeamNames 
} from "../../api/helpers"
import { setUserData } from "../../auth/authActions"

interface TeamMember {
  user_id: string
  name: string
  email: string
  user_role: string
  view_team_ids: string[]
  view_team_names: string[]
}

interface OrganizationUsersResponse {
  success: boolean
  requesting_user_id: string
  organization_id: string
  user_role: string
  total_users: number
  users: TeamMember[]
}

interface Team {
  team_id: string
  team_name: string
}

export function TeamSettings() {
  const dispatch = useDispatch()
  const user = useSelector((state: any) => state.auth.user)
  
  // State for data
  const [members, setMembers] = useState<TeamMember[]>([])
  const [availableTeams, setAvailableTeams] = useState<Team[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  
  // State for invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteRole, setInviteRole] = useState<string>("user")
  const [inviteTeamAccess, setInviteTeamAccess] = useState<string[]>([])
  const [isInviting, setIsInviting] = useState(false)
  
  // State for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [editRole, setEditRole] = useState<string>("user")
  const [editTeamAccess, setEditTeamAccess] = useState<string[]>([])
  const [isEditing, setIsEditing] = useState(false)
  
  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null)

  // Load user data and organization users
  useEffect(() => {
    const loadData = async () => {
      console.log("🔍 TeamSettings - Current user state:", user)
      console.log("🔍 TeamSettings - User ID:", user?._id)
      
      let currentUserId = user?._id
      let currentOrgId = user?.organization_info?._id

      // If no user ID, try to load user data
      if (!currentUserId) {
        console.log("🔄 TeamSettings - Loading user data...")
        try {
          const userData = await getUser(dispatch)
          console.log("🔍 TeamSettings - getUser() response:", userData)
          
          if (!userData?.user?._id) {
            setError("Unable to load user data")
            setIsLoading(false)
            return
          }
          
          dispatch(setUserData(userData.user))
          currentUserId = userData.user._id
          currentOrgId = userData.user.organization_info?._id
        } catch (err) {
          console.error("❌ TeamSettings - Failed to load user data:", err)
          setError("Failed to load user data")
          setIsLoading(false)
          return
        }
      }

      setUserId(currentUserId)
      setOrganizationId(currentOrgId)
      setIsLoading(true)
      setError(null)

      try {
        // Fetch organization users
        console.log("🔍 TeamSettings - Fetching organization users for user:", currentUserId)
        const response = await getOrganizationUsersByUserId(currentUserId)
        console.log("✅ TeamSettings - Response:", response)
        
        if (response?.success) {
          setMembers(response.users || [])
          setIsAdmin(response.user_role === "admin" || response.user_role === "super_admin")
        } else {
          setError(response?.error || "Failed to load team data")
        }

        // Fetch available teams
        if (currentOrgId) {
          console.log("🔍 TeamSettings - Fetching teams for organization:", currentOrgId)
          const teamsResponse = await getTeamNames(currentOrgId)
          console.log("✅ TeamSettings - Teams response:", teamsResponse)
          
          if (teamsResponse?.success && teamsResponse?.team_names) {
            setAvailableTeams(teamsResponse.team_names)
          }
        }
      } catch (err: any) {
        console.error("❌ TeamSettings - Error:", err)
        console.error("❌ TeamSettings - Error details:", err.response?.data)
        setError(err.response?.data?.error || err.response?.data?.message || "Failed to load team members")
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [dispatch])

  const handleInviteUser = async () => {
    if (!inviteEmail) return

    setIsInviting(true)
    try {
      console.log("🔄 TeamSettings - Inviting user:", { inviteEmail, inviteRole, inviteTeamAccess })
      
      // Use registration-user endpoint
      await postRegistrationUser({
        email: inviteEmail,
        role: inviteRole,
        view_team_ids: inviteTeamAccess.length > 0 ? inviteTeamAccess : undefined,
      })

      console.log("✅ TeamSettings - User invited successfully")
      
      // Reload the members list
      if (userId) {
        const response = await getOrganizationUsersByUserId(userId)
        if (response?.success) {
          setMembers(response.users || [])
        }
      }

      // Reset and close
      setInviteDialogOpen(false)
      setInviteEmail("")
      setInviteRole("user")
      setInviteTeamAccess([])
    } catch (err: any) {
      console.error("❌ TeamSettings - Error inviting user:", err)
      alert(err.response?.data?.message || "Failed to invite user")
    } finally {
      setIsInviting(false)
    }
  }

  const handleEditUser = async () => {
    if (!editingMember) return

    setIsEditing(true)
    try {
      console.log("🔄 TeamSettings - Editing user:", { userId: editingMember.user_id, editRole, editTeamAccess })
      
      await updateOrganizationUser(editingMember.user_id, {
        user_role: editRole,
        view_team_ids: editTeamAccess.length > 0 ? editTeamAccess : undefined,
      })

      console.log("✅ TeamSettings - User updated successfully")

      // Update local state
      setMembers(
        members.map((member) =>
          member.user_id === editingMember.user_id 
            ? { ...member, user_role: editRole, view_team_ids: editTeamAccess } 
            : member
        )
      )

      // Close dialog
      setEditDialogOpen(false)
      setEditingMember(null)
    } catch (err: any) {
      console.error("❌ TeamSettings - Error editing user:", err)
      alert(err.response?.data?.message || "Failed to update user")
    } finally {
      setIsEditing(false)
    }
  }

  const confirmRemoveMember = (member: TeamMember) => {
    setMemberToDelete(member)
    setDeleteDialogOpen(true)
  }

  const handleRemoveMember = async () => {
    if (!memberToDelete) return

    try {
      console.log("🔄 TeamSettings - Removing user:", memberToDelete.user_id)
      
      await deleteOrganizationUser(memberToDelete.user_id)

      console.log("✅ TeamSettings - User removed successfully")

      // Update local state
      setMembers(members.filter((member) => member.user_id !== memberToDelete.user_id))
      
      // Close dialog and reset
      setDeleteDialogOpen(false)
      setMemberToDelete(null)
    } catch (err: any) {
      console.error("❌ TeamSettings - Error removing user:", err)
      // You could add a toast notification here instead of alert
      setDeleteDialogOpen(false)
      setMemberToDelete(null)
    }
  }

  const openEditDialog = (member: TeamMember) => {
    console.log("🔍 Opening edit dialog for member:", member)
    setEditingMember(member)
    
    // Map backend roles to frontend select values
    // Only admin and super_admin are "admin", everything else is "user"
    const mappedRole = (member.user_role === 'admin' || member.user_role === 'super_admin') 
      ? 'admin' 
      : 'user'
    setEditRole(mappedRole)
    
    // Set team access - will pre-check the checkboxes
    setEditTeamAccess(member.view_team_ids || [])
    setEditDialogOpen(true)
  }

  const toggleTeamAccess = (teamId: string, isInvite: boolean) => {
    if (isInvite) {
      setInviteTeamAccess((prev) => 
        prev.includes(teamId) ? prev.filter((t) => t !== teamId) : [...prev, teamId]
      )
    } else {
      setEditTeamAccess((prev) => 
        prev.includes(teamId) ? prev.filter((t) => t !== teamId) : [...prev, teamId]
      )
    }
  }

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      super_admin: "Admin",
      admin: "Admin",
      manager: "Manager",
      rep: "User",
      user: "User",
    }
    return roleMap[role] || "User"
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Team Settings</h2>
            <p className="text-muted-foreground">Manage users, roles, and team access permissions</p>
          </div>
        </div>
        <Card className="p-6 flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading team members...</div>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Team Settings</h2>
            <p className="text-muted-foreground">Manage users, roles, and team access permissions</p>
          </div>
        </div>
        <Card className="p-6 flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-red-500 text-center">
            <p className="font-semibold mb-2">{error}</p>
            <p className="text-sm text-muted-foreground">Check the browser console for more details</p>
          </div>
          <Button onClick={() => window.location.reload()} variant="outline" size="sm">
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Team Settings</h2>
          <p className="text-muted-foreground">Manage users, roles, and team access permissions</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setInviteDialogOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Invite User
          </Button>
        )}
      </div>

      <Card className="p-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Team Access</TableHead>
              {isAdmin && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length > 0 ? (
              members.map((member) => (
                <TableRow key={member.user_id}>
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={member.user_role === "admin" || member.user_role === "super_admin" ? "default" : "secondary"} 
                      className="gap-1"
                    >
                      {(member.user_role === "admin" || member.user_role === "super_admin") && (
                        <Shield className="h-3 w-3" />
                      )}
                      {getRoleDisplayName(member.user_role)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {member.view_team_names && member.view_team_names.length > 0 ? (
                        member.view_team_names.map((teamName, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {teamName}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">No team</span>
                      )}
                    </div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(member)} 
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {member.user_id !== userId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => confirmRemoveMember(member)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-muted-foreground">
                  No team members found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
            <DialogDescription>
              Send an invitation to a new team member. They'll receive an email with instructions to join.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={(value: string) => setInviteRole(value)}>
                <SelectTrigger id="role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Team Access</Label>
              <div className="text-xs text-muted-foreground mb-2">
                Leave empty for access to all teams
              </div>
              <div className="grid grid-cols-2 gap-3">
                {availableTeams.map((team) => (
                  <div key={team.team_id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`invite-team-${team.team_id}`}
                      checked={inviteTeamAccess.includes(team.team_id)}
                      onCheckedChange={() => toggleTeamAccess(team.team_id, true)}
                    />
                    <Label htmlFor={`invite-team-${team.team_id}`} className="cursor-pointer font-normal">
                      {team.team_name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteUser} disabled={!inviteEmail || isInviting}>
              {isInviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit User Permissions</DialogTitle>
            <DialogDescription>
              Update the role and team access for {editingMember?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editRole} onValueChange={(value: string) => setEditRole(value)}>
                <SelectTrigger id="edit-role" className="w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Team Access</Label>
              <div className="text-xs text-muted-foreground mb-2">
                Leave empty for access to all teams
              </div>
              <div className="grid grid-cols-2 gap-3">
                {availableTeams.map((team) => (
                  <div key={team.team_id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-team-${team.team_id}`}
                      checked={editTeamAccess.includes(team.team_id)}
                      onCheckedChange={() => toggleTeamAccess(team.team_id, false)}
                    />
                    <Label htmlFor={`edit-team-${team.team_id}`} className="cursor-pointer font-normal">
                      {team.team_name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditUser} disabled={isEditing}>
              {isEditing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{memberToDelete?.name}</strong> from your organization? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
